import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Checkbox, FormControlLabel, IconButton, Popover, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { INK, NEUTRAL } from "../../../../theme/tokens";
import type { Job } from "../../../../utils/models";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { StaffingTotalsMode } from "./monthlyJobUtils";

type Props = {
	totalsMode: StaffingTotalsMode;
	onTotalsModeChange: (mode: StaffingTotalsMode) => void;
	enabledJobs: Job[];
	visibleJobIds: ReadonlySet<string>;
	onVisibleJobsChange: (jobIds: string[]) => void;
};

/**
 * One-line explanation per totals mode. The three modes were redefined when the invented
 * Morning/Evening/Night taxonomy was replaced by the department's real shift-time windows, so the labels
 * alone no longer say what changes — only the ACTIVE mode's hint is rendered, to keep the popover short.
 */
const TOTALS_MODE_HINT_KEY: Record<StaffingTotalsMode, string> = {
	[StaffingTotalsMode.ByJob]: "monthly.view.totals.by.job.hint",
	[StaffingTotalsMode.ByShift]: "monthly.view.totals.by.shift.hint",
	[StaffingTotalsMode.JobAndShift]: "monthly.view.totals.both.hint",
};

export const MonthlyViewConfigButton: FC<Props> = ({ totalsMode, onTotalsModeChange, enabledJobs, visibleJobIds, onVisibleJobsChange }) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const toggleJob = (jobId: string, checked: boolean) => {
		const next = new Set(visibleJobIds);
		if (checked) next.add(jobId);
		else if (next.size > 1) next.delete(jobId); // Keep at least one job visible.
		onVisibleJobsChange(Array.from(next));
	};

	return (
		<>
			<InHouseTooltip title={t("monthly.view.settings")}>
				<IconButton
					size="small"
					aria-label={t("monthly.view.settings")}
					onClick={(event) => setAnchorEl(event.currentTarget)}
					data-testid="monthly-view-settings-button"
				>
					<SettingsOutlinedIcon sx={{ fontSize: 20, color: INK.SECONDARY }} />
				</IconButton>
			</InHouseTooltip>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box data-testid="monthly-view-settings-popover" sx={{ p: 2, width: 280 }}>
					<Typography sx={{ fontSize: "12px", fontWeight: 700, color: INK.SECONDARY, textTransform: "uppercase", mb: 1 }}>
						{t("monthly.view.totals.mode")}
					</Typography>
					<ToggleButtonGroup
						exclusive
						fullWidth
						size="small"
						value={totalsMode}
						onChange={(_, value) => value && onTotalsModeChange(value as StaffingTotalsMode)}
						data-testid="monthly-view-totals-mode"
					>
						<ToggleButton value={StaffingTotalsMode.ByJob} sx={{ fontSize: "11px", textTransform: "none" }}>
							{t("monthly.view.totals.by.job")}
						</ToggleButton>
						<ToggleButton value={StaffingTotalsMode.ByShift} sx={{ fontSize: "11px", textTransform: "none" }}>
							{t("monthly.view.totals.by.shift")}
						</ToggleButton>
						<ToggleButton value={StaffingTotalsMode.JobAndShift} sx={{ fontSize: "11px", textTransform: "none" }}>
							{t("monthly.view.totals.both")}
						</ToggleButton>
					</ToggleButtonGroup>
					<Typography data-testid="monthly-view-totals-mode-hint" sx={{ fontSize: "11px", color: NEUTRAL[600], mt: 0.75, lineHeight: 1.4 }}>
						{t(TOTALS_MODE_HINT_KEY[totalsMode])}
					</Typography>

					<Typography sx={{ fontSize: "12px", fontWeight: 700, color: INK.SECONDARY, textTransform: "uppercase", mt: 2, mb: 0.5 }}>
						{t("monthly.view.visible.jobs")}
					</Typography>
					<Box sx={{ display: "flex", flexDirection: "column", maxHeight: 220, overflowY: "auto" }}>
						{enabledJobs.length === 0 && <Typography sx={{ fontSize: "12px", color: NEUTRAL[500] }}>{t("monthly.view.no.jobs.configured")}</Typography>}
						{enabledJobs.map((job) => (
							<FormControlLabel
								key={job.id}
								control={
									<Checkbox
										size="small"
										checked={visibleJobIds.has(job.id)}
										onChange={(event) => toggleJob(job.id, event.target.checked)}
										data-testid={`monthly-view-job-toggle-${job.id}`}
									/>
								}
								label={<Typography sx={{ fontSize: "13px" }}>{job.displayName}</Typography>}
							/>
						))}
					</Box>
				</Box>
			</Popover>
		</>
	);
};
